import React from 'react';
import { Bar, Pie, Doughnut, Line } from 'react-chartjs-2';
import { Chart, ArcElement, BarElement, CategoryScale, LinearScale, Tooltip, Legend, LineElement, PointElement, Filler } from 'chart.js';

Chart.register(ArcElement, BarElement, CategoryScale, LinearScale, Tooltip, Legend, LineElement, PointElement, Filler);

function buildPalette(size) {
  if (size <= 0) return [];
  
  const elegantHues = [
    { h: 190, s: 90, l: 60 }, // Cyan
    { h: 260, s: 80, l: 65 }, // Purple
    { h: 150, s: 70, l: 55 }, // Mint
    { h: 330, s: 80, l: 65 }, // Pink
    { h: 210, s: 90, l: 65 }, // Light Blue
    { h: 40,  s: 90, l: 65 }, // Gold/Peach
    { h: 280, s: 70, l: 60 }, // Deep Purple
    { h: 170, s: 80, l: 50 }, // Teal
  ];

  const colors = [];
  for (let i = 0; i < size; i++) {
    const base = elegantHues[i % elegantHues.length];
    // Slightly shift hue for very large datasets to avoid repeating exactly
    const hueShift = Math.floor(i / elegantHues.length) * 15;
    const finalHue = (base.h + hueShift) % 360;
    
    colors.push({
      fill: `hsla(${finalHue}, ${base.s}%, ${base.l}%, 0.5)`,
      border: `hsla(${finalHue}, ${base.s}%, ${base.l}%, 0.9)`,
    });
  }
  return colors;
}

const sharedOptions = {
  responsive: true,
  maintainAspectRatio: false,
  animation: {
    duration: 800,
    easing: 'easeOutQuart'
  },
  plugins: {
    legend: {
      labels: {
        color: 'rgba(255, 255, 255, 0.7)',
        font: { size: 12, family: 'Inter, sans-serif' },
        usePointStyle: true,
        padding: 20
      },
    },
    tooltip: {
      backgroundColor: 'rgba(10, 15, 30, 0.85)',
      titleColor: 'rgba(255, 255, 255, 0.9)',
      bodyColor: 'rgba(255, 255, 255, 0.7)',
      borderColor: 'rgba(116, 242, 255, 0.1)',
      borderWidth: 1,
      padding: 12,
      cornerRadius: 8,
      displayColors: true,
      boxPadding: 4,
      backdropFilter: 'blur(8px)'
    },
  },
};

const glassContainerStyle = {
  width: '100%', 
  height: '100%', 
  background: 'linear-gradient(135deg, rgba(255,255,255,0.02) 0%, rgba(255,255,255,0.005) 100%)',
  border: '1px solid rgba(255,255,255,0.03)',
  borderRadius: '16px',
  boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.15)',
  backdropFilter: 'blur(10px)',
  padding: '1.5rem',
  position: 'relative'
};

export function LeadCharts({ leads, only, salesTimeline }) {
  // Segmentação por categoria
  const segmentCounts = {};
  const emailCounts = { email: 0, whatsapp: 0, ambos: 0, nenhum: 0 };
  const regionCounts = {};

  leads.forEach(l => {
    // Segmentação
    const seg = l.Categoria || 'N/A';
    segmentCounts[seg] = (segmentCounts[seg] || 0) + 1;
    // Região
    const reg = l.Estado || 'N/A';
    regionCounts[reg] = (regionCounts[reg] || 0) + 1;
    // Contato
    const hasEmail = l.Emails && l.Emails !== 'N/A';
    const hasWhats = l.WhatsApp && l.WhatsApp !== 'N/A';
    if (hasEmail && hasWhats) emailCounts.ambos++;
    else if (hasEmail) emailCounts.email++;
    else if (hasWhats) emailCounts.whatsapp++;
    else emailCounts.nenhum++;
  });

  const segLabels = Object.keys(segmentCounts);
  const segPalette = buildPalette(segLabels.length);

  const regLabels = Object.keys(regionCounts);
  const regPalette = buildPalette(regLabels.length);

  const segData = {
    labels: segLabels,
    datasets: [{
      label: 'Segmentação',
      data: Object.values(segmentCounts),
      backgroundColor: segPalette.map(c => c.fill),
      borderColor: segPalette.map(c => c.border),
      borderWidth: 1,
      borderRadius: 4,
      hoverBackgroundColor: segPalette.map(c => c.fill.replace('0.5', '0.7')),
    }]
  };

  const contatoData = {
    labels: ['E-mail', 'WhatsApp', 'Ambos', 'Nenhum'],
    datasets: [{
      label: 'Contatos',
      data: [emailCounts.email, emailCounts.whatsapp, emailCounts.ambos, emailCounts.nenhum],
      backgroundColor: [
        'rgba(0, 242, 254, 0.6)',   // Email
        'rgba(37, 211, 102, 0.6)',  // WhatsApp
        'rgba(161, 140, 209, 0.6)', // Ambos
        'rgba(255, 255, 255, 0.05)' // Nenhum
      ],
      borderColor: [
        'rgba(0, 242, 254, 0.9)',
        'rgba(37, 211, 102, 0.9)',
        'rgba(161, 140, 209, 0.9)',
        'rgba(255, 255, 255, 0.1)'
      ],
      borderWidth: 1,
      hoverOffset: 4
    }]
  };

  const regData = {
    labels: regLabels,
    datasets: [{
      label: 'Por Região',
      data: Object.values(regionCounts),
      backgroundColor: regPalette.map(c => c.fill),
      borderColor: regPalette.map(c => c.border),
      borderWidth: 1,
    }]
  };

  if (only === 'segmentacao') {
    return (
      <div style={{ width: 540, height: 360, maxWidth: '92vw' }}>
        <div style={glassContainerStyle}>
          <Bar
            data={segData}
            options={{
              ...sharedOptions,
              plugins: { ...sharedOptions.plugins, legend: { display: false } },
              scales: {
                x: { ticks: { color: 'var(--text-secondary)' }, grid: { color: 'rgba(255, 255, 255, 0.03)' } },
                y: { ticks: { color: 'var(--text-secondary)' }, grid: { color: 'rgba(255, 255, 255, 0.03)' } },
              },
            }}
          />
        </div>
      </div>
    );
  }
  if (only === 'contatos') {
    return (
      <div style={{ width: 460, height: 360, maxWidth: '92vw' }}>
        <div style={glassContainerStyle}>
          <Doughnut data={contatoData} options={{...sharedOptions, cutout: '70%'}} />
        </div>
      </div>
    );
  }
  if (only === 'regiao') {
    return (
      <div style={{ width: 520, height: 400, maxWidth: '92vw' }}>
        <div style={glassContainerStyle}>
          <Pie data={regData} options={sharedOptions} />
        </div>
      </div>
    );
  }
  if (only === 'vendas') {
    const timeline = salesTimeline || [];
    if (!timeline.length) {
      return <p style={{ color: 'var(--text-muted)', textAlign: 'center', padding: '2rem' }}>Sem dados de vendas no período selecionado.</p>;
    }
    const salesData = {
      labels: timeline.map(d => {
        const [, m, day] = d.date.split('-');
        return `${day}/${m}`;
      }),
      datasets: [
        {
          label: 'Leads vendidos',
          data: timeline.map(d => d.leads),
          borderColor: 'rgba(116, 242, 255, 1)',
          backgroundColor: 'rgba(116, 242, 255, 0.12)',
          fill: true,
          tension: 0.35,
          pointRadius: 3,
          pointBackgroundColor: 'rgba(116, 242, 255, 1)',
        },
        {
          label: 'Pedidos',
          data: timeline.map(d => d.orders),
          borderColor: 'rgba(216, 255, 62, 0.8)',
          backgroundColor: 'rgba(216, 255, 62, 0.06)',
          fill: true,
          tension: 0.35,
          pointRadius: 3,
          pointBackgroundColor: 'rgba(216, 255, 62, 1)',
        },
      ],
    };
    return (
      <div style={{ width: 600, height: 360, maxWidth: '92vw' }}>
        <div style={glassContainerStyle}>
          <Line
            data={salesData}
            options={{
              ...sharedOptions,
              scales: {
                x: {
                  ticks: { color: 'var(--text-secondary)', maxRotation: 45 },
                  grid: { color: 'rgba(255, 255, 255, 0.03)' },
                },
                y: {
                  ticks: { color: 'var(--text-secondary)' },
                  grid: { color: 'rgba(255, 255, 255, 0.03)' },
                  beginAtZero: true,
                },
              },
            }}
          />
        </div>
      </div>
    );
  }
  // Default: mostrar todos
  return null;
}

export function LeadSummary({ leads }) {
  // Números finais
  const total = leads.length;
  const emails = leads.filter(l => l.Emails && l.Emails !== 'N/A').length;
  const whats = leads.filter(l => l.WhatsApp && l.WhatsApp !== 'N/A').length;
  const ambos = leads.filter(l => l.Emails && l.Emails !== 'N/A' && l.WhatsApp && l.WhatsApp !== 'N/A').length;
  const semContato = leads.filter(l => (!l.Emails || l.Emails === 'N/A') && (!l.WhatsApp || l.WhatsApp === 'N/A')).length;
  return (
    <div style={{ display: 'flex', gap: 32, margin: '2rem 0', justifyContent: 'center' }}>
      <div className="glass" style={{ fontSize: '2.8rem', fontWeight: 900, color: 'var(--accent)', padding: '1.5rem 2.5rem', borderRadius: 18 }}>
        {total} Leads Exportados
      </div>
      <div className="glass" style={{ fontSize: '1.2rem', color: 'var(--accent-hot)', padding: '1.5rem 2.5rem', borderRadius: 18 }}>
        {emails} com E-mail<br/>{whats} com WhatsApp<br/>{ambos} com ambos<br/>{semContato} sem contato
      </div>
    </div>
  );
}
